'use client';

import { DashboardData } from '@/types/api';
import MetricCard from './metric-card';

const ShipIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
  </svg>
);

const RouteIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const CompassIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" strokeWidth={2} />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity="0.3" stroke="currentColor" strokeWidth={1.5} />
  </svg>
);

interface MetricsSectionProps {
  data: DashboardData;
}

export default function MetricsSection({ data }: MetricsSectionProps) {
  const activeVoyagers = data?.total_active_voyagers ?? (data?.user_voyagers || []).filter((v) => v.status === 1).length;
  const totalPlanned = data?.user_voyagers?.length ?? 0;
  const ongoingVessels = data?.latest_vessel_condition?.ongoing ?? data?.latest_vessel_condition?.sail ?? 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Total Devices"
        value={data?.total_devices ?? 0}
        icon={<ShipIcon />}
        delay={0}
        tone="primary"
        hint={
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {data?.total_visible_devices ?? data?.total_devices ?? 0} Shown
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              {data?.total_hidden_devices ?? 0} Hidden
            </span>
          </div>
        }
      />
      <MetricCard
        title="Active Voyagers & ETA"
        value={activeVoyagers}
        icon={<RouteIcon />}
        delay={100}
        tone="info"
        hint={
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {ongoingVessels} Ongoing
            </span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span className="text-xs text-sky-600 dark:text-sky-400 font-medium">
              {totalPlanned} Planned
            </span>
          </div>
        }
      />
      <MetricCard
        title="Offline Devices"
        value={data?.total_offline_devices ?? 0}
        delay={200}
        tone="danger"
        hint="Needs attention"
      />
      <MetricCard
        title="Expired Vessels"
        value={data?.expired_vessels?.length ?? 0}
        delay={300}
        tone="warning"
        hint="Subscription ended"
      />
    </div>
  );
}
